import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import classNames from 'classnames';

import { Modal } from '@components/common';

import CloseIcon from '@assets/icons/close.svg';
import FilterIcon from '@assets/icons/filter.svg';
import SortIcon from '@assets/icons/sort.svg';

import { ResultText } from '../ResultText';
import { SORT_METHODS_PRODUCTS } from '../SortProducts/constants';

export const FilterMobile = ({
  categoriesPath,
  filtersList,
  search,
  hasFilters,
  totalProducts
}) => {
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isSortModalOpen, setIsSortModalOpen] = useState(false);

  const router = useRouter();
  const pathname = usePathname();
  const urlSearchParams = useSearchParams();
  const searchParams = Object.fromEntries(urlSearchParams.entries());

  const sortAttribute =
    searchParams.sortAttribute || SORT_METHODS_PRODUCTS[0].value.attribute;
  const sortDirection =
    searchParams.sortDirection || SORT_METHODS_PRODUCTS[0].value.sortDirection;

  // State is mantainted only to reflect visual changes faster.
  // The source of truth for the sort is actually the URL parameter.
  const [sortValue, setSortValue] = useState({ sortAttribute, sortDirection });

  useEffect(() => {
    setSortValue({ sortAttribute, sortDirection });
  }, [sortAttribute, sortDirection]);

  return (
    <div className="lg:hidden w-full">
      <Modal
        handleToggle={() => setIsFilterModalOpen(false)}
        isVisible={isFilterModalOpen}
        className="max-w-[360px] bg-white"
        scrollContent
      >
        <div className="pt-5 mx-5 border-b border-gray-light pb-4 flex items-center justify-between">
          <div className="flex items-center">
            <FilterIcon className="text-gray-darkest mr-5" width={24} />
            <h2 className="text-gray-dark">Filtrar pesquisa por:</h2>
          </div>
          <button onClick={() => setIsFilterModalOpen(false)}>
            <CloseIcon width={24} height={24} />
          </button>
        </div>
        <div className="py-4 px-5 flex justify-end">
          <Link
            href={
              categoriesPath()
                ? `/${categoriesPath()}/c`
                : `/search?query=${search}&page=1`
            }
            prefetch={false}
            onClick={() => {
              setIsFilterModalOpen(false);
            }}
            className="text-base text-gray-dark disabled:text-gray-light underline"
          >
            <FormattedMessage
              id="filterModal.action"
              defaultMessage="Clear all"
            />
          </Link>
        </div>
        <ul className="gap-y-6 grid px-5 pb-9">{filtersList}</ul>
      </Modal>
      <Modal
        handleToggle={() => setIsSortModalOpen(false)}
        isVisible={isSortModalOpen}
        className="max-w-[360px] bg-white"
        scrollContent
      >
        <div className="pt-5 mx-5 border-b border-gray-light pb-4 flex items-center justify-between">
          <div className="flex items-center">
            <SortIcon className="text-gray-darkest mr-5" width={24} />
            <h2 className="text-gray-dark">Ordenar por:</h2>
          </div>
          <button onClick={() => setIsSortModalOpen(false)}>
            <CloseIcon width={24} height={24} />
          </button>
        </div>
        <div className="gap gap-y-4 grid mx-5 mt-6">
          {SORT_METHODS_PRODUCTS.map(sortMethod => {
            const isChecked =
              sortMethod.value.attribute === sortValue.sortAttribute &&
              sortMethod.value.sortDirection === sortValue.sortDirection;

            return (
              <label
                className={classNames(
                  "text-sm text-gray-darkest flex items-center cursor-pointer before:w-6 before:h-6 before:content-[''] before:mr-3 before:bg-primary-lightest before:rounded-full before:border-2 before:border-primary-medium",
                  {
                    'before:shadow-[_inset_0_0_0_.35em_#E3F7FF,inset_0_0_0_1em_#009ee0]':
                      isChecked
                  }
                )}
                key={sortMethod.value.id}
              >
                <input
                  className="cursor-pointer sr-only"
                  type="radio"
                  name="sort"
                  value={sortMethod.value.id}
                  onChange={() => {
                    const changedSort = {
                      sortDirection: sortMethod.value.sortDirection,
                      sortAttribute: sortMethod.value.attribute
                    };

                    setSortValue(changedSort);

                    const nextURLParams = new URLSearchParams(
                      urlSearchParams.toString()
                    );

                    nextURLParams.set(
                      'sortDirection',
                      changedSort.sortDirection
                    );

                    nextURLParams.set(
                      'sortAttribute',
                      changedSort.sortAttribute
                    );

                    router.push(`${pathname}?${nextURLParams.toString()}`);
                  }}
                />
                {sortMethod.label}
              </label>
            );
          })}
        </div>
      </Modal>
      <div className="grid">
        <ResultText search={search} totalProducts={totalProducts} />

        {totalProducts > 0 && (
          <div className="flex justify-end mt-5 mb-4">
            <div className="flex justify-end gap-x-4">
              <button
                className="flex justify-center items-center gap-x-1 uppercase text-gray-darkest text-sm font-bold tracking-wider"
                onClick={() => setIsSortModalOpen(true)}
              >
                <FormattedMessage
                  id="productSort.sortButton"
                  defaultMessage="Sort"
                />

                <SortIcon width={24} height={24} />
              </button>

              {hasFilters && (
                <div className="flex items-center justify-center">
                  <button
                    className="flex justify-center items-center gap-x-[6px] uppercase text-gray-darkest text-sm font-bold tracking-wider"
                    onClick={() => setIsFilterModalOpen(true)}
                  >
                    <FormattedMessage
                      id="searchPage.filterButton"
                      defaultMessage="Filter"
                    />

                    <FilterIcon width={18} height={18} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
